import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { articlesAPI, adminAPI, uploadAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, ArrowRight, Image as ImageIcon, Upload } from 'lucide-react';

const AdminArticles = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    id: '',
    title_fa: '',
    title_en: '',
    content: '',
    excerpt: '',
    image_url: '',
    author: '',
    reading_time: '',
    tags: '',
  });

  useEffect(() => {
    const verifyAndFetch = async () => {
      try {
        await adminAPI.verify();
        await fetchArticles();
      } catch (error) {
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };
    verifyAndFetch();
  }, [navigate]);

  const fetchArticles = async () => {
    try {
      const response = await articlesAPI.getAll();
      setArticles(response.data);
    } catch (error) {
      toast.error('خطا در دریافت مقالات');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('حجم فایل نباید بیشتر از 5 مگابایت باشد');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('فقط فایل‌های تصویری مجاز هستند');
      return;
    }

    setUploading(true);
    try {
      const response = await uploadAPI.uploadImage(file);
      const imageUrl = process.env.REACT_APP_BACKEND_URL + response.data.url;
      setFormData({ ...formData, image_url: imageUrl });
      toast.success('تصویر با موفقیت آپلود شد');
    } catch (error) {
      toast.error('خطا در آپلود تصویر');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const articleData = {
        ...formData,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        date: new Date().toISOString(),
      };

      if (editingArticle) {
        await articlesAPI.update(formData.id, articleData);
        toast.success('مقاله با موفقیت ویرایش شد');
      } else {
        articleData.id = Date.now().toString();
        await articlesAPI.create(articleData);
        toast.success('مقاله با موفقیت ایجاد شد');
      }

      setShowModal(false);
      resetForm();
      fetchArticles();
    } catch (error) {
      toast.error('خطا در ذخیره مقاله');
    }
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
    setFormData({
      ...article,
      tags: article.tags?.join(', ') || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این مقاله اطمینان دارید؟')) return;

    try {
      await articlesAPI.delete(id);
      toast.success('مقاله با موفقیت حذف شد');
      fetchArticles();
    } catch (error) {
      toast.error('خطا در حذف مقاله');
    }
  };

  const resetForm = () => {
    setFormData({
      id: '',
      title_fa: '',
      title_en: '',
      content: '',
      excerpt: '',
      image_url: '',
      author: '',
      reading_time: '',
      tags: '',
    });
    setEditingArticle(null);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="text-[#D4AF37]">در حال بارگذاری...</div></div>;
  }

  return (
    <div className="min-h-screen bg-[#121212] py-8" data-testid="admin-articles-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link
              to="/admin/dashboard"
              className="text-[#D4AF37] hover:text-[#F3E5AB] transition-colors"
            >
              <ArrowRight size={24} />
            </Link>
            <h1 className="text-3xl font-light text-white">مدیریت مقالات</h1>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black rounded-lg font-medium hover:bg-[#F3E5AB] transition-colors"
          >
            <Plus size={20} />
            مقاله جدید
          </button>
        </div>

        {/* Articles List */}
        <div className="space-y-4">
          {articles.length === 0 ? (
            <div className="glass-effect border border-white/10 rounded-xl p-12 text-center">
              <p className="text-zinc-400 mb-4">هنوز مقاله‌ای ثبت نشده است</p>
              <button
                onClick={() => setShowModal(true)}
                className="text-[#D4AF37] hover:text-[#F3E5AB] transition-colors"
              >
                اولین مقاله را اضافه کنید
              </button>
            </div>
          ) : (
            articles.map((article) => (
              <div key={article.id} className="glass-effect border border-white/10 rounded-xl p-6 hover:border-[#D4AF37]/30 transition-all">
                <div className="flex items-start gap-6">
                  {/* Image */}
                  <div className="w-32 h-32 rounded-lg overflow-hidden bg-white/5 flex-shrink-0">
                    {article.image_url ? (
                      <img src={article.image_url} alt={article.title_fa} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="text-zinc-600" size={32} />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="text-xl font-medium text-white mb-2">{article.title_fa}</h3>
                    <p className="text-sm text-zinc-400 mb-3 line-clamp-2">{article.excerpt}</p>
                    <div className="flex items-center gap-4 text-xs text-zinc-500">
                      {article.author && <span>نویسنده: {article.author}</span>}
                      {article.reading_time && <span>زمان مطالعه: {article.reading_time}</span>}
                      {article.tags && article.tags.length > 0 && (
                        <span className="flex gap-2">
                          {article.tags.map((tag) => (
                            <span key={`${article.id}-${tag}`} className="px-2 py-1 bg-white/5 rounded">#{tag}</span>
                          ))}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(article)}
                      className="p-2 text-[#D4AF37] hover:bg-white/5 rounded-lg transition-colors"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(article.id)}
                      className="p-2 text-red-400 hover:bg-white/5 rounded-lg transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
            <div className="glass-effect border border-white/10 rounded-2xl p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-2xl font-light text-white mb-6">
                {editingArticle ? 'ویرایش مقاله' : 'مقاله جدید'}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Image Upload */}
                <div>
                  <label className="block text-sm text-zinc-300 mb-2">تصویر شاخص</label>
                  <div className="space-y-4">
                    {formData.image_url && (
                      <div className="relative w-full h-48 rounded-lg overflow-hidden">
                        <img src={formData.image_url} alt="Preview" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <label className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-zinc-300 hover:bg-white/10 cursor-pointer transition-colors">
                      <Upload size={20} />
                      {uploading ? 'در حال آپلود...' : 'آپلود تصویر جدید'}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploading}
                      />
                    </label>
                    <input
                      type="url"
                      name="image_url"
                      value={formData.image_url}
                      onChange={handleChange}
                      placeholder="یا لینک تصویر را وارد کنید"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-zinc-300 mb-2">عنوان (فارسی) *</label>
                    <input
                      type="text"
                      name="title_fa"
                      required
                      value={formData.title_fa}
                      onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-300 mb-2">عنوان (انگلیسی)</label>
                    <input
                      type="text"
                      name="title_en"
                      value={formData.title_en}
                      onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-zinc-300 mb-2">خلاصه مقاله *</label>
                  <textarea
                    name="excerpt"
                    required
                    rows="2"
                    value={formData.excerpt}
                    onChange={handleChange}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none resize-none"
                  ></textarea>
                </div>

                <div>
                  <label className="block text-sm text-zinc-300 mb-2">محتوای کامل مقاله *</label>
                  <textarea
                    name="content"
                    required
                    rows="10"
                    value={formData.content}
                    onChange={handleChange}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none resize-none"
                  ></textarea>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-zinc-300 mb-2">نویسنده</label>
                    <input
                      type="text"
                      name="author"
                      value={formData.author}
                      onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-300 mb-2">زمان مطالعه (مثلاً: 5 دقیقه)</label>
                    <input
                      type="text"
                      name="reading_time"
                      value={formData.reading_time}
                      onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-zinc-300 mb-2">برچسب‌ها (با کاما جدا کنید)</label>
                  <input
                    type="text"
                    name="tags"
                    value={formData.tags}
                    onChange={handleChange}
                    placeholder="مهاجرت، تحصیل، ویزا"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
                  />
                </div>

                <div className="flex gap-4">
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-[#D4AF37] text-black rounded-lg font-medium hover:bg-[#F3E5AB] transition-colors"
                  >
                    {editingArticle ? 'ذخیره تغییرات' : 'ایجاد مقاله'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-6 py-3 border border-white/20 text-white rounded-lg hover:bg-white/5 transition-colors"
                  >
                    انصراف
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminArticles;
